import { Modal } from 'antd'
import {
  UPLOAD_QUEUE_COPY,
  UPLOAD_QUEUE_PICK_CAP,
  evaluateUploadPickPreflight,
} from 'src/utils/invoiceUploadQueueHelpers'

/**
 * Chunk U1 — pick-cap / quota gate before reading files or POSTing the batch.
 * Never starts a doomed upload. Quota overflow may offer “Use first N” (explicit).
 *
 * @returns {Promise<{
 *   proceed: boolean,
 *   reason: string|null,
 *   allowedCount: number,
 *   useFirstN: boolean,
 * }>}
 */
export function promptUploadPickPreflight({
  selectedCount,
  remainingQuota,
  pickCap = UPLOAD_QUEUE_PICK_CAP,
} = {}) {
  const result = evaluateUploadPickPreflight({
    selectedCount,
    remainingQuota,
    pickCap,
  })

  if (result.ok) {
    return Promise.resolve({
      proceed: true,
      reason: null,
      allowedCount: result.allowedCount,
      useFirstN: false,
    })
  }

  if (result.reason === 'empty') {
    return Promise.resolve({
      proceed: false,
      reason: 'empty',
      allowedCount: 0,
      useFirstN: false,
    })
  }

  if (result.reason === 'pick_cap') {
    return new Promise(resolve => {
      Modal.info({
        title: UPLOAD_QUEUE_COPY.pickCapTitle,
        content: result.message || UPLOAD_QUEUE_COPY.pickCapBody(pickCap),
        okText: UPLOAD_QUEUE_COPY.pickCapOk,
        centered: true,
        onOk: () =>
          resolve({
            proceed: false,
            reason: 'pick_cap',
            allowedCount: result.allowedCount,
            useFirstN: false,
          }),
        onCancel: () =>
          resolve({
            proceed: false,
            reason: 'pick_cap',
            allowedCount: result.allowedCount,
            useFirstN: false,
          }),
      })
    })
  }

  if (result.reason === 'quota_exhausted') {
    // Caller opens PlanLimitModal (upsell) — do not start the batch.
    return Promise.resolve({
      proceed: false,
      reason: 'quota_exhausted',
      allowedCount: 0,
      useFirstN: false,
    })
  }

  if (result.reason === 'quota_exceeded') {
    const remaining = result.allowedCount
    return new Promise(resolve => {
      Modal.confirm({
        title: UPLOAD_QUEUE_COPY.quotaBlockTitle,
        content:
          UPLOAD_QUEUE_COPY.quotaSelectFirstBody(selectedCount, remaining) ||
          result.message,
        okText: UPLOAD_QUEUE_COPY.quotaSelectFirstOk(remaining),
        cancelText: UPLOAD_QUEUE_COPY.quotaBlockOk,
        centered: true,
        onOk: () =>
          resolve({
            proceed: true,
            reason: 'quota_exceeded',
            allowedCount: remaining,
            useFirstN: true,
          }),
        onCancel: () =>
          resolve({
            proceed: false,
            reason: 'quota_exceeded',
            allowedCount: remaining,
            useFirstN: false,
          }),
      })
    })
  }

  return Promise.resolve({
    proceed: false,
    reason: result.reason,
    allowedCount: result.allowedCount,
    useFirstN: false,
  })
}

export default promptUploadPickPreflight
